import { Box, Grid, Stack, Typography } from '@mui/material';
import { KpiTile, SegmentedToggle } from 'src/sections/manager/report-dashboard/reportKit';
import { ROLE_OPTIONS, SOURCE_OPTIONS, summarize } from '../users-kpi-model';

/**
 * The summary strip above the user table: who is in this organization, and how many of them put
 * themselves there through the public invite link.
 *
 * The tiles describe the FILTERED rows, the same array the table below renders, so the two can
 * never disagree about a number the manager is looking at -- which is why `users` here is the
 * filtered list and `allUsers` is passed separately for the captions that have to speak about
 * the organization as a whole.
 *
 * There is deliberately no date filter. The one date question worth asking on this screen is
 * "did anyone sign themselves up today", and that is a line on the invite tile rather than a
 * control a manager has to find and then set back.
 */

const GRID_PROPS = { xs: 12, sm: 6, md: 3 };

const share = (count, total) =>
  total > 0 ? `${Math.round((count * 100) / total)}% of members` : undefined;

const FilterGroup = ({ label, options, value, onChange }) => (
  <Stack gap={0.75}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
      {label}
    </Typography>
    <SegmentedToggle options={options} value={value} onChange={onChange} />
  </Stack>
);

const UsersKpis = ({ users = [], allUsers = [], organizationId, filters, onChange, loading }) => {
  const summary = summarize(users, organizationId);
  const orgWide = summarize(allUsers, organizationId);

  const set = (key) => (value) => onChange({ ...filters, [key]: value });

  /**
   * "of N in this organization", but only when N is actually a different number.
   *
   * The caption exists to warn that a tile is showing a slice rather than the whole
   * organization. A filter that happens not to exclude anything from THIS tile is not a slice,
   * and repeating the value underneath it just reads as a third number to reconcile.
   */
  const outOf = (shown, total) =>
    shown === total ? undefined : `of ${total.toLocaleString()} in this organization`;

  return (
    <Box sx={{ px: 2, pt: 2 }}>
      <Stack direction="row" flexWrap="wrap" gap={3} sx={{ mb: 2 }}>
        <FilterGroup
          label="Role"
          options={ROLE_OPTIONS}
          value={filters.role}
          onChange={set('role')}
        />
        <FilterGroup
          label="Joined via"
          options={SOURCE_OPTIONS}
          value={filters.source}
          onChange={set('source')}
        />
      </Stack>

      <Grid container spacing={2}>
        <KpiTile
          label="Members"
          value={summary.total.toLocaleString()}
          caption={outOf(summary.total, orgWide.total)}
          gridProps={GRID_PROPS}
          loading={loading}
        />
        <KpiTile
          label="Managers"
          value={summary.managers.toLocaleString()}
          caption={share(summary.managers, summary.total)}
          gridProps={GRID_PROPS}
          loading={loading}
        />
        <KpiTile
          label="Volunteers"
          value={summary.volunteers.toLocaleString()}
          caption={share(summary.volunteers, summary.total)}
          gridProps={GRID_PROPS}
          loading={loading}
        />
        <KpiTile
          label="Invite sign-ups"
          value={summary.inviteSignups.toLocaleString()}
          caption={`${summary.inviteSignupsToday.toLocaleString()} today`}
          caption2={outOf(summary.inviteSignups, orgWide.inviteSignups)}
          gridProps={GRID_PROPS}
          loading={loading}
        />
      </Grid>
    </Box>
  );
};

export default UsersKpis;
